import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useReagendarOS } from '@/hooks/useOrdensServico';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  CalendarClock, 
  Calendar as CalendarIcon,
  Loader2, 
  AlertTriangle,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface ReagendarServicoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  osId: string;
  numeroOs: string;
  dataAtual?: string | null;
}

export function ReagendarServicoDialog({
  open,
  onOpenChange,
  osId,
  numeroOs,
  dataAtual,
}: ReagendarServicoDialogProps) {
  const { t } = useTranslation();
  const [novaData, setNovaData] = useState<Date | undefined>();
  const [hora, setHora] = useState('');
  const [motivo, setMotivo] = useState('');
  const [calendarOpen, setCalendarOpen] = useState(false);

  const reagendarOS = useReagendarOS();

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      if (dataAtual) {
        const dataParseada = parseISO(dataAtual);
        setNovaData(dataParseada);
        setHora(format(dataParseada, 'HH:mm'));
      } else {
        setNovaData(undefined);
        setHora('');
      }
      setMotivo('');
    }
  }, [open, dataAtual]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!novaData || !motivo.trim()) return;

    // Combinar data e hora
    const [horaNum, minutoNum] = hora.split(':').map(Number);
    const dataCompleta = new Date(novaData);
    dataCompleta.setHours(horaNum || 10, minutoNum || 0, 0, 0);

    reagendarOS.mutate(
      {
        id: osId,
        novaData: dataCompleta.toISOString(),
        motivo: motivo.trim(),
      },
      {
        onSuccess: () => {
          onOpenChange(false);
        },
      }
    );
  };

  const isFormValid = novaData && hora && motivo.trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-primary" />
            {t('orders.reschedule.title')}
          </DialogTitle>
          <DialogDescription>
            {t('orders.reschedule.description', { number: numeroOs })}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Data atual */}
          {dataAtual && (
            <div className="bg-muted/50 p-3 rounded-md">
              <p className="text-sm text-muted-foreground">{t('orders.reschedule.currentDate')}</p>
              <p className="font-medium">
                {format(parseISO(dataAtual), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
              </p>
            </div>
          )}

          {/* Nova data */}
          <div className="space-y-2">
            <Label>{t('orders.reschedule.newDate')} *</Label>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    'w-full justify-start text-left font-normal',
                    !novaData && 'text-muted-foreground'
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {novaData ? format(novaData, 'PPP', { locale: ptBR }) : t('orders.reschedule.selectDate')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={novaData}
                  onSelect={(date) => {
                    setNovaData(date);
                    setCalendarOpen(false);
                  }}
                  disabled={(date) => date < new Date()}
                  initialFocus
                  locale={ptBR}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Hora */}
          <div className="space-y-2">
            <Label htmlFor="hora">{t('orders.reschedule.time')} *</Label>
            <Input
              id="hora"
              type="time"
              value={hora}
              onChange={(e) => setHora(e.target.value)}
              required
            />
          </div>

          {/* Motivo */}
          <div className="space-y-2">
            <Label htmlFor="motivo">{t('orders.reschedule.reasonLabel')} *</Label>
            <Textarea
              id="motivo"
              placeholder={t('orders.reschedule.reasonPlaceholder')}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              rows={3}
              required
            />
            <p className="text-xs text-muted-foreground">
              {t('orders.reschedule.reasonNote')}
            </p>
          </div>

          {/* Aviso */}
          <div className="flex items-start gap-2 p-3 bg-warning/10 border border-warning/30 rounded-md">
            <AlertTriangle className="h-4 w-4 text-warning mt-0.5 flex-shrink-0" />
            <p className="text-sm text-warning">
              {t('orders.reschedule.warning')}
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={reagendarOS.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={reagendarOS.isPending || !isFormValid}
            >
              {reagendarOS.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('orders.reschedule.rescheduling')}
                </>
              ) : (
                <>
                  <CalendarClock className="h-4 w-4 mr-2" />
                  {t('orders.reschedule.confirm')}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
